import { useContext } from 'react'

import { MobXProviderContext, useLocalObservable } from 'mobx-react'

import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { getWorkflowTypeCreatedByCaption } from '~/client/src/shared/enums/WorkflowTypeCreatedBy'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import { IFormTypeRow } from '../types'

interface ProvidedStores {
  permitTypesStore: WorkflowTypesStore
  formCategoriesStore: WorkflowCategoriesStore
  projectDateStore: ProjectDateStore
  state: DesktopInitialState
}

export interface FormTypeRecordsResult {
  rows: IFormTypeRow[]
  loading: boolean
  // Category ids in display order (name-sorted active categories) — feeds the
  // band ordering when grouping by category.
  categoryOrder: string[]
}

const LOADING_EVENTS = [
  e.LOAD_AND_LISTEN_TO_PERMIT_TYPES,
  e.LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES,
]

// Rows are derived in a MobX computed (`useLocalObservable` getters compile
// to computeds): store reads stay tracked through the `observer` caller, and
// the result keeps one reference per data version — the identity stability
// TanStack consumers and dep arrays need. Mirrors useUserRecords.
export function useFormTypeRecords(): FormTypeRecordsResult {
  const { permitTypesStore, formCategoriesStore, projectDateStore, state } =
    useContext(MobXProviderContext) as unknown as ProvidedStores

  const local = useLocalObservable(() => ({
    get derived(): Omit<FormTypeRecordsResult, 'loading'> {
      const rows: IFormTypeRow[] = permitTypesStore.actualTypes.map(
        formType => ({
          id: formType.id,
          type: formType.type,
          name: formType.name || '',
          iconName: formType.iconName,
          // Resolve through the store so a soft-deleted (or unknown) category
          // collapses to UNASSIGNED — otherwise its stale, still-non-empty id
          // would form its own band/facet, splitting deleted-category rows into
          // multiple "Unassigned" groups that don't merge or cross-filter.
          categoryId:
            formCategoriesStore.getById(formType.categoryId)?.id || UNASSIGNED,
          categoryName: formType.getCategoryName(formCategoriesStore),
          isEnabled: formType.isEnabled,
          isAdminOnly: formType.isAdminOnly,
          createdBy: formType.createdBy,
          createdByCaption: getWorkflowTypeCreatedByCaption(formType.createdBy),
          lastUpdatedDisplay: formType.getFormattedUpdatedAt(
            projectDateStore.getMonthDayYearAndTimeToDisplay,
          ),
          lastUpdatedSortValue:
            !formType.isDefault && formType.updatedAt ? formType.updatedAt : 0,
          isDeletable: formType.isDeletable,
        }),
      )

      const categoryOrder = formCategoriesStore.activeCategories.map(
        category => category.id,
      )

      return { rows, categoryOrder }
    },
  }))

  const loading = LOADING_EVENTS.some(ev => state.loading.get(ev))
  const { rows, categoryOrder } = local.derived

  return { rows, loading, categoryOrder }
}
